import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  isOriginInUse,
  isStockInUse,
  isVariantsInUse,
  limitFilterOptionsBasedOnListings,
} from '../../util/search';
import { getConversionRates } from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { limitSearchOptions, limitSearchBaseOptions } from '../../config/configSearch';
import { listingFields } from '../../config/configListing';
import { currenciesHelper as currency } from '../../helpers/currencies';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 36;
const brandId = process.env.REACT_APP_BRAND_NAME;
const REACT_APP_CURRENCY_SWITCH_ENABLED = process.env.REACT_APP_CURRENCY_SWITCH_ENABLED === 'true';

const distinctProductIds = response => {
  const uniqueListings = response.data.data.reduce((acc, current) => {
    const productId = current.attributes.publicData?.gtProductId;
    const currentPrice = current.attributes.price.amount / 100;

    if (productId) {
      if (acc[productId]) {
        // Update the entry if the current price is lower
        if (currentPrice < acc[productId].attributes.price.amount / 100) {
          acc[productId] = current;
        }
      } else {
        // Initialize the entry
        acc[productId] = current;
      }
    } else {
      // Handle listings without a productId
      acc[`noId-${current?.id?.uuid}`] = current;
    }
    return acc;
  }, {});

  const filteredListings = Object.values(uniqueListings);

  const maxPaginationDisplay = 20;
  const totalItems = response?.data?.meta?.totalItems;
  const totalPages = Math.ceil(totalItems / RESULT_PAGE_SIZE);
  const page = response.data.meta.page;
  const paginationLimit = Math.max(1, Math.min(maxPaginationDisplay, Math.floor(totalPages / 2)));

  return {
    data: {
      data: filteredListings,
      meta: {
        ...response?.data?.meta,
        totalItems,
        totalPages,
        page,
        paginationLimit,
        perPage: RESULT_PAGE_SIZE,
      },
      included: response?.data?.included,
    },
  };
};

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_FILTER_OPTIONS_REQUEST = 'app/SearchPage/FETCH_FILTER_OPTIONS_REQUEST';
export const FETCH_FILTER_OPTIONS_SUCCESS = 'app/SearchPage/FETCH_FILTER_OPTIONS_SUCCESS';
export const FETCH_FILTER_OPTIONS_ERROR = 'app/SearchPage/FETCH_FILTER_OPTIONS_ERROR';

export const FETCH_CONVERSION_RATES = 'app/SearchPage/FETCH_CONVERSION_RATES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  filterOptions: {},
  conversionRates: null,
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FETCH_FILTER_OPTIONS_SUCCESS:
      return { ...state, filterOptions: payload.options };

    case FETCH_CONVERSION_RATES:
      return { ...state, conversionRates: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchConversionRates = payload => ({
  type: FETCH_CONVERSION_RATES,
  payload,
});

export const searchListings = (searchParams, config, conversionRates) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const { perPage, price, dates, sort, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const isVariantsEnabled = isVariantsInUse(config);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };
  const brandFilter = { pub_brandId: brandId };

  let params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    ...brandFilter,
    perPage,
  };

  const priceValues = params?.price?.split(',');

  let currentCurrency = currency.get();
  const conversionRate = conversionRates?.[currentCurrency] || 1;

  if (priceValues && conversionRate) {
    const priceValueMin = priceValues?.[0];
    const priceValueMax = priceValues?.[1];

    const minConverted = (priceValueMin / conversionRate).toFixed();
    const maxConverted = (priceValueMax / conversionRate).toFixed();

    params.price = `${minConverted},${maxConverted}`;
  }

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      let updatedResponse = response;

      if (isVariantsEnabled) {
        updatedResponse = distinctProductIds(response);
      }

      dispatch(addMarketplaceEntities(updatedResponse, sanitizeConfig));
      dispatch(searchListingsSuccess(updatedResponse));
      return updatedResponse;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  let conversionRates = null;

  if (REACT_APP_CURRENCY_SWITCH_ENABLED) {
    await getConversionRates({})
      .then(response => {
        dispatch(fetchConversionRates(response));
        conversionRates = response;
        return response;
      })
      .catch(e => {
        console.log(e);
      });
  }

  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'publicData',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.originalGtPrice',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config,
    conversionRates
  );

  return dispatch(searchListingsCall);
};

export const fetchFilterOptionsSuccess = options => ({
  type: FETCH_FILTER_OPTIONS_SUCCESS,
  payload: { options },
});

// Thunk action to fetch filter options
export const fetchFilterOptions = () => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_FILTER_OPTIONS_REQUEST });

  // Fetch all pages recursively
  // Note: this will have performance issues for large number of listings
  const fetchPage = (page = 1, accumulatedListings = []) => {
    const params = {
      pub_brandId: brandId,
      perPage: 100,
      include: 'fields.listing.publicData', // load all publicData for possible filters
      minStock: 1,
      stockMode: 'match-undefined', // only available listings
      page,
    };

    sdk.listings
      .query(params)
      .then(res => {
        const listings = res.data.data;
        const newAccumulatedListings = accumulatedListings.concat(listings);
        const { totalPages, page: currentPage } = res.data.meta;

        if (currentPage < totalPages) {
          fetchPage(page + 1, newAccumulatedListings); // Fetch next page
        } else {
          // Last page has been fetched, process all listings
          const limitedFilters = limitFilterOptionsBasedOnListings(
            listingFields,
            newAccumulatedListings,
            limitSearchOptions,
            limitSearchBaseOptions
          );
          dispatch(fetchFilterOptionsSuccess(limitedFilters));
        }
      })
      .catch(e => {
        dispatch({ type: FETCH_FILTER_OPTIONS_ERROR, payload: e });
        console.error('Fetching filter options failed:', e);
      });
  };

  fetchPage();
};
